import {
  Box,
  Heading,
  HStack,
  ListItem,
  Text,
  UnorderedList,
  VStack
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { usePricingControllerGetSessionCreditPricing } from '../../../../core/api/__generated__/public-api'

export interface PlanData {
  id: string
  title: string
  priceId: string
  price: number
  features: string[]
  isRecommended?: boolean
  isComingSoon?: boolean
}

export const PlanCard = ({
  title,
  price,
  features,
  isSelected,
  onClick,
  isComingSoon,
  showCurrentTrial,
  showCurrentPlan
}: {
  title: string
  price: number
  features: string[]
  isRecommended?: boolean
  isSelected?: boolean
  onClick: () => void
  isComingSoon?: boolean
  showCurrentTrial?: boolean
  showCurrentPlan?: boolean
}) => (
  <Box
    cursor={isComingSoon ? 'not-allowed' : 'pointer'}
    border="2px solid"
    borderColor={isSelected ? '#2D54E8' : '#F9F9F9'}
    onClick={isComingSoon ? undefined : onClick}
    position="relative"
    borderRadius="12px"
    p="5"
    bg={isComingSoon ? '#F9F9F9' : 'white'}
    _hover={
      !isComingSoon
        ? {
            borderColor: isSelected ? '#2D54E8' : 'blue.300'
          }
        : undefined
    }
    minH="200px"
  >
    <VStack spacing="5" align="stretch" h="full">
      <Box textAlign="left">
        <HStack align="center" mb="2">
          <Heading fontSize="20px" fontWeight="bold" color="black">
            {title}
          </Heading>
          {showCurrentTrial && (
            <Box
              bg="#2D54E826"
              color="#2D54E8"
              px="2"
              py="1"
              borderRadius="4px"
              fontSize="11px"
              fontWeight="500"
            >
              Current trial
            </Box>
          )}
          {showCurrentPlan && (
            <Box
              bg="#2D54E826"
              color="#2D54E8"
              px="2"
              py="1"
              borderRadius="4px"
              fontSize="11px"
              fontWeight="500"
            >
              Current
            </Box>
          )}
          {isComingSoon && (
            <Box
              bg="#3D41450F"
              color="#757575"
              px="2"
              py="1"
              borderRadius="4px"
              fontSize="11px"
              fontWeight="500"
            >
              Coming soon!
            </Box>
          )}
        </HStack>

        <Text fontSize="20px" fontWeight="bold" color="#2D54E8">
          ${price.toFixed(2)}{' '}
          <Text as="span" fontSize="14px" fontWeight="normal">
            per session
          </Text>
        </Text>
      </Box>
      <VStack spacing="3" align="stretch" flex="1">
        <Text fontSize="14px" fontWeight="600" color="black">
          Includes:
        </Text>
        <UnorderedList
          spacing="2"
          pl="0"
          fontSize="14px"
          color="gray.600"
          fontWeight="400"
          lineHeight="1.4"
          listStyleType="none"
          sx={{
            '& li': {
              position: 'relative',
              paddingLeft: '15px',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '0px',
                top: '0.55em',
                width: '5.5px',
                height: '5.5px',
                backgroundColor: 'gray.400',
                borderRadius: '50%'
              }
            }
          }}
        >
          {features.map(feature => (
            <ListItem key={feature}>{feature}</ListItem>
          ))}
        </UnorderedList>
      </VStack>
    </VStack>
  </Box>
)

export const useSessionPlans = () => {
  const { data: pricing, isLoading: isPricingLoading } =
    usePricingControllerGetSessionCreditPricing<{
      standard: { priceId: string; amount: number }
      plus: { priceId: string; amount: number }
      pro: { priceId: string; amount: number }
    }>()
  const plans: PlanData[] = useMemo(
    () => [
      {
        id: 'standard',
        title: 'Standard',
        priceId: pricing?.standard?.priceId || '',
        features: ['Automated documentation', 'Native telehealth'],
        price: pricing?.standard?.amount || 0.49
      },
      {
        id: 'plus',
        title: 'Plus',
        priceId: pricing?.plus?.priceId || '',
        features: [
          'Automated documentation',
          'Native telehealth',
          'Clinical decision support'
        ],
        price: pricing?.plus?.amount || 0.99,
        isRecommended: true
      },
      {
        id: 'pro',
        title: 'Pro',
        priceId: pricing?.pro?.priceId || '',
        features: [
          'Automated documentation',
          'Native telehealth',
          'Clinical decision support',
          'AI-assisted EHR'
        ],
        price: pricing?.pro?.amount || 1.49,
        isComingSoon: true
      }
    ],
    [pricing]
  )

  return { plans, isPricingLoading }
}
